import { useMemo } from "react";
import { Newspaper } from "lucide-react";

/**
 * Below-transcript panel aggregating every outlet cited in the capsule.
 * Sorted by frequency; each outlet links to the first cited article
 * (best-effort — one source can appear across several stories).
 */
export default function SourcesPanel({ segments = [] }) {
  const rollup = useMemo(() => {
    const map = new Map();
    for (const s of segments) {
      const name = (s.source_hint || "").trim();
      if (!name) continue;
      const key = name.toLowerCase();
      const existing = map.get(key);
      if (existing) {
        existing.count += 1;
        if (!existing.url && s.source_url) existing.url = s.source_url;
      } else {
        map.set(key, { name, url: s.source_url || "", count: 1 });
      }
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [segments]);

  if (rollup.length === 0) return null;

  const total = rollup.reduce((n, r) => n + r.count, 0);

  return (
    <section data-testid="sources-panel" className="mt-16 border-t border-zinc-950 pt-8">
      <div className="mb-4 flex items-baseline gap-3">
        <span className="font-mono text-xs uppercase tracking-[0.3em] text-rose-600">A3</span>
        <h2 className="text-xs font-semibold uppercase tracking-[0.25em] text-zinc-500">
          Sources today
        </h2>
      </div>
      <div className="flex items-start gap-4">
        <div className="hidden pt-1 md:block">
          <Newspaper className="h-5 w-5 text-zinc-400" />
        </div>
        <div className="flex-1">
          <p className="mb-4 text-sm text-zinc-500">
            {rollup.length === 1
              ? `${total} ${total === 1 ? "story" : "stories"} in this capsule cited by one outlet.`
              : `${total} stories in this capsule cited across ${rollup.length} outlets.`}
          </p>
          <ul className="flex flex-wrap gap-2" data-testid="sources-list">
            {rollup.map((r, i) => {
              const label = (
                <>
                  <span>{r.name}</span>
                  {r.count > 1 && (
                    <span className="ml-1.5 rounded-full bg-rose-600 px-1.5 py-0.5 font-mono text-[10px] text-white">
                      {r.count}
                    </span>
                  )}
                </>
              );
              return (
                <li key={r.name} data-testid={`source-chip-${i}`}>
                  {r.url ? (
                    <a
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center border border-zinc-300 bg-white px-3 py-1.5 text-sm text-zinc-950 transition-colors hover:border-rose-600 hover:text-rose-600"
                    >
                      {label}
                    </a>
                  ) : (
                    <span className="inline-flex items-center border border-zinc-300 bg-white px-3 py-1.5 text-sm text-zinc-950">
                      {label}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}
